import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { uploadAttachment } from '../../../../server/api';
import { uploadOssFile } from '../../../../server/oss-upload';
import Toast from '@components/Toast';
import { MAX_FEEDBACK_IMAGES } from '../../constants/feedbackPostLimits';
import { isSupportedImageFile } from '../../../../utils/imageFile';

export interface FeedbackUploadedAttachment {
    type: number;
    id: number;
    url: string;
    localUrl?: string;
}

const toastSuccess = (msg: string) => {
    try {
        Toast?.success ? Toast.success(msg) : alert(msg);
    } catch {
        alert(msg);
    }
};

const toastError = (msg: string) => {
    try {
        Toast?.error ? Toast.error(msg) : alert(msg);
    } catch {
        alert(msg);
    }
};

const toastInfo = (msg: string) => {
    try {
        (Toast as any)?.info ? Toast.info(msg) : alert(msg);
    } catch {
        alert(msg);
    }
};

export function useFeedbackAttachments(setPost: any) {
    const [uploadingImages, setUploadingImages] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const imageCaptureInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);

    const postData = setPost?.postData || {};

    const imageAttachments = useMemo(() => {
        const list = postData.uploadedAttachments || [];
        return list.filter((item: FeedbackUploadedAttachment) => item.type === 1 && item.id);
    }, [postData.uploadedAttachments]);

    const videoUrl = postData.video || '';
    const hasVideo = !!videoUrl || !!(postData.videoId && Number(postData.videoId) > 0);
    const hasImages = imageAttachments.length > 0;

    const canUploadImages = !hasVideo && imageAttachments.length < MAX_FEEDBACK_IMAGES;
    const canUploadVideo = !hasImages && !hasVideo;

    const syncAttachments = useCallback((nextList: FeedbackUploadedAttachment[]) => {
        setPost?.setPostData?.({ uploadedAttachments: nextList });
    }, [setPost]);

    const removeImage = useCallback((id: number) => {
        const current = postData.uploadedAttachments || [];
        syncAttachments(current.filter((item: FeedbackUploadedAttachment) => item.id !== id));
    }, [postData.uploadedAttachments, syncAttachments]);

    const removeVideo = useCallback(() => {
        if (setPost?.setVideo) {
            setPost.setVideo('');
        }
        const current = postData.uploadedAttachments || [];
        setPost?.setPostData?.({
            videoId: 0,
            videoCoverUrl: '',
            uploadedAttachments: current.filter((item: FeedbackUploadedAttachment) => item.type !== 3),
        });
    }, [postData.uploadedAttachments, setPost]);

    const uploadFiles = useCallback(async (files: File[]) => {
        if (!files.length) return;

        if (hasVideo) {
            toastInfo('已上传视频，不能再上传图片');
            return;
        }

        const remainingSlots = MAX_FEEDBACK_IMAGES - imageAttachments.length;
        if (remainingSlots <= 0) {
            toastError(`最多上传 ${MAX_FEEDBACK_IMAGES} 张图片`);
            return;
        }

        const filesToUpload = files.slice(0, remainingSlots);
        if (filesToUpload.length < files.length) {
            toastError(`最多上传 ${MAX_FEEDBACK_IMAGES} 张图片，超出的图片未添加`);
        }

        const closeLoading = Toast.loading?.({
            content: filesToUpload.length > 1 ? '图片上传中，请稍候...' : '图片上传中...',
            duration: 0,
        });

        setUploadingImages(true);
        try {
            const current = postData.uploadedAttachments || [];
            let nextAttachments = [...current];

            for (const file of filesToUpload) {
                if (!isSupportedImageFile(file)) {
                    toastError('请选择图片文件');
                    continue;
                }
                if (file.size > 50 * 1024 * 1024) {
                    toastError('图片大小不能超过 50MB');
                    continue;
                }

                const formData = new FormData();
                formData.append('type', '1');
                formData.append('file', file);

                const response = await uploadAttachment(formData);
                const imageData = response.data as { id?: number; url?: string; webpUrl?: string } | undefined;
                const imageUrl = imageData?.webpUrl || imageData?.url;

                if (response.code === 0 && imageUrl && imageData?.id) {
                    nextAttachments = [
                        ...nextAttachments,
                        {
                            type: 1,
                            id: imageData.id,
                            url: imageUrl,
                            localUrl: imageUrl,
                        },
                    ];
                } else {
                    toastError(response.message || '图片上传失败');
                }
            }

            if (nextAttachments.length !== current.length) {
                syncAttachments(nextAttachments);
            }
        } finally {
            closeLoading?.();
            setUploadingImages(false);
        }
    }, [hasVideo, imageAttachments.length, postData.uploadedAttachments, syncAttachments]);

    const uploadVideoFile = useCallback(async (file: File) => {
        if (hasImages) {
            toastInfo('已上传图片，不能再上传视频');
            return;
        }
        if (hasVideo) {
            toastInfo('只可添加1个视频');
            return;
        }
        if (!file.type.startsWith('video/')) {
            toastError('请选择视频文件');
            return;
        }
        if (file.size > 500 * 1024 * 1024) {
            toastError('视频大小不能超过 500MB');
            return;
        }

        const closeLoading = Toast.loading?.({
            content: '视频上传中，请稍候...',
            duration: 0,
        });

        setUploadingVideo(true);
        try {
            const uploadDir = `public/videos/${new Date().toISOString().split('T')[0].replace(/-/g, '/')}`;
            const result = await uploadOssFile(file, uploadDir);

            if (result.code !== 0) {
                toastError(result.msg || '视频上传失败');
                return;
            }

            const videoData = result.data || {};
            const ossVideoUrl = videoData.url || videoData.ossVideoUrl;
            const videoId = videoData.id;

            if (!ossVideoUrl) {
                toastError('视频上传失败');
                return;
            }

            if (setPost?.setVideo) {
                setPost.setVideo(ossVideoUrl);
            }

            const current = postData.uploadedAttachments || [];
            const withoutVideo = current.filter((item: FeedbackUploadedAttachment) => item.type !== 3);
            const nextAttachments = videoId
                ? [...withoutVideo, { type: 3, id: videoId, url: ossVideoUrl, localUrl: ossVideoUrl }]
                : withoutVideo;

            setPost?.setPostData?.({
                ...(videoId ? { videoId } : {}),
                ...(videoData.videoCoverUrl ? {
                    videoCoverUrl: videoData.videoCoverUrl,
                    videoCoverData: videoData,
                } : {}),
                uploadedAttachments: nextAttachments,
            });

            toastSuccess('视频上传成功');
        } catch (error: any) {
            toastError(error?.message || '视频上传失败，请重试');
        } finally {
            closeLoading?.();
            setUploadingVideo(false);
        }
    }, [hasImages, hasVideo, postData.uploadedAttachments, setPost]);

    const openImagePicker = useCallback(() => {
        if (!canUploadImages) {
            if (hasVideo) {
                toastInfo('已上传视频，不能再上传图片');
            } else {
                toastError(`最多上传 ${MAX_FEEDBACK_IMAGES} 张图片`);
            }
            return;
        }
        imageInputRef.current?.click();
    }, [canUploadImages, hasVideo]);

    const openImageCapturePicker = useCallback(() => {
        if (!canUploadImages) {
            if (hasVideo) {
                toastInfo('已上传视频，不能再上传图片');
            } else {
                toastError(`最多上传 ${MAX_FEEDBACK_IMAGES} 张图片`);
            }
            return;
        }
        imageCaptureInputRef.current?.click();
    }, [canUploadImages, hasVideo]);

    const openVideoPicker = useCallback(() => {
        if (!canUploadVideo) {
            if (hasVideo) {
                toastInfo('只可添加1个视频');
            } else if (hasImages) {
                toastInfo('已上传图片，不能再上传视频');
            }
            return;
        }
        videoInputRef.current?.click();
    }, [canUploadVideo, hasImages, hasVideo]);

    const handleImageInputChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        await uploadFiles(files);
        e.target.value = '';
    }, [uploadFiles]);

    const handleVideoInputChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            await uploadVideoFile(file);
        }
        e.target.value = '';
    }, [uploadVideoFile]);

    return {
        imageAttachments,
        videoUrl,
        hasVideo,
        hasImages,
        canUploadImages,
        canUploadVideo,
        uploadingImages,
        uploadingVideo,
        imageInputRef,
        imageCaptureInputRef,
        videoInputRef,
        openImagePicker,
        openImageCapturePicker,
        openVideoPicker,
        removeImage,
        removeVideo,
        handleImageInputChange,
        handleVideoInputChange,
        maxFeedbackImages: MAX_FEEDBACK_IMAGES,
    };
}
